import React from 'react'
import SEO from '../components/SEO'
import BookingForm from '../components/BookingForm'
import { useCms } from '../context/CmsContext'

export default function BookingPage() {
  const { siteSettings, getSection, getPage } = useCms()
  const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(siteSettings.fullAddress)}`

  const pageData = getPage('booking', {})

  const heroSection = {
    title: pageData.heroTitle || getSection('booking_hero').title || 'Booking & Kontakt',
    subtitle: pageData.heroSubtitle || getSection('booking_hero').subtitle || 'Udfyld formularen herunder for at sende en uforpligtende bookingforespørgsel, eller ring direkte til mig ved hurtige spørgsmål.'
  }

  return (
    <div className="min-h-screen bg-[#0f1013] text-[#f4f3f0] py-12 md:py-20">
      <SEO
        title="Booking & Kontakt | Book Tid til Tatovering - Docuit ink"
        description="Book din næste tatovering hos Docuit ink i Vester Sottrup nær Sønderborg. Udfyld bookingformularen med dine ønsker og referencebilleder, eller ring direkte til mig på 28 90 75 50."
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h1 className="text-2xl sm:text-4xl lg:text-5xl font-serif font-extrabold text-white mb-4">
            {heroSection.title}
          </h1>
          {heroSection.subtitle && (
            <p className="text-neutral-400 text-sm sm:text-base leading-relaxed">
              {heroSection.subtitle}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {/* Main Booking Form Column */}
          <div className="lg:col-span-8">
            <BookingForm />
          </div>

          {/* Contact Details & Info Sidebar */}
          <div className="lg:col-span-4 space-y-8">
            
            {/* Direct Contact Card */}
            <div className="space-y-6">
              <h3 className="text-lg font-serif font-bold text-white border-b border-neutral-800 pb-3">
                Kontaktoplysninger
              </h3>

              <ul className="space-y-4 text-xs">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center text-[#cbb05e] shrink-0 pt-0.5">
                    <i className="fa-solid fa-location-dot" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="text-neutral-400 font-mono block text-[11px] uppercase">Adresse</span>
                    <span className="text-white font-medium">{siteSettings.fullAddress}</span>
                  </div>
                </li>

                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center text-[#cbb05e] shrink-0 pt-0.5">
                    <i className="fa-solid fa-phone" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="text-neutral-400 font-mono block text-[11px] uppercase">Telefon</span>
                    <a
                      href={`tel:${siteSettings.phoneRaw}`}
                      className="text-white hover:text-[#cbb05e] transition-colors font-medium whitespace-nowrap"
                    >
                      <span className="whitespace-nowrap">{siteSettings.phone}</span>
                    </a>
                  </div>
                </li>

                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center text-[#cbb05e] shrink-0 pt-0.5">
                    <i className="fa-solid fa-envelope" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="text-neutral-400 font-mono block text-[11px] uppercase">E-mail</span>
                    <a
                      href={`mailto:${siteSettings.email}`}
                      className="text-white hover:text-[#cbb05e] transition-colors font-medium whitespace-nowrap"
                    >
                      <span className="whitespace-nowrap">{siteSettings.email}</span>
                    </a>
                  </div>
                </li>

                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center text-[#cbb05e] shrink-0 pt-0.5">
                    <i className="fa-solid fa-clock" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="text-neutral-400 font-mono block text-[11px] uppercase">Åbningstider</span>
                    <span className="text-white font-medium">{siteSettings.openingHours}</span>
                  </div>
                </li>

                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center text-[#cbb05e] shrink-0 pt-0.5">
                    <i className="fa-solid fa-id-card" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="text-neutral-400 font-mono block text-[11px] uppercase">CVR-nummer</span>
                    <span className="text-white font-medium">{siteSettings.cvr}</span>
                  </div>
                </li>
              </ul>

              <div className="pt-2">
                <a
                  href={siteSettings.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#1877F2]/10 border border-[#1877F2]/40 hover:bg-[#1877F2]/20 text-white rounded-full text-xs font-semibold transition-colors"
                >
                  <i className="fa-brands fa-facebook text-base text-white" aria-hidden="true" />
                  Besøg mig på Facebook
                </a>
              </div>
            </div>

            {/* How booking works box */}
            <div className="bg-[#f5f4f0] border border-[#e2dec6] rounded-xl p-6 text-[#141519] shadow-sm">
              <h4 className="text-xs uppercase tracking-widest text-[#141519] font-mono font-bold mb-3 flex items-center gap-2">
                <i className="fa-solid fa-circle-info text-[#141519]" aria-hidden="true" />
                Hvordan behandles din forespørgsel?
              </h4>
              <ol className="space-y-3 text-xs text-neutral-700 list-decimal list-inside leading-relaxed font-medium">
                <li>Jeg gennemgår dit ønske, din størrelse og vedhæftede billeder.</li>
                <li>Jeg kontakter dig pr. telefon eller e-mail for at drøfte tidsforbrug, design og pris.</li>
                <li>Når alle detaljer er på plads, aftales og bekræftes den endelige tid.</li>
              </ol>
            </div>

            {/* Non-cookie Location Card with external link */}
            <div className="text-center py-4">
              <i className="fa-solid fa-map-location-dot text-3xl text-[#cbb05e] mb-3" aria-hidden="true" />
              <h4 className="text-sm font-serif font-bold text-white mb-1">Find mig i Vester Sottrup nær Sønderborg</h4>
              <p className="text-xs text-neutral-400 mb-4">{siteSettings.fullAddress}</p>
              <a
                href={mapUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-neutral-900 border border-neutral-700 hover:border-[#cbb05e] text-white rounded text-xs uppercase font-semibold tracking-wider transition-colors"
              >
                Åbn kort i Google Maps
                <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" aria-hidden="true" />
              </a>
            </div>

          </div>

        </div>

      </div>
    </div>
  )
}
